/**
 * Conference Revenue Service
 * Handles revenue and room calculations based on selected conference and year
 */

import { YEAR_CONFIG } from '../config/yearConfig';

/**
 * Calculate conference-specific revenue for a client
 * @param {string} clientName - Client name (Customer rename from sponsor sheet)
 * @param {Array} sponsorData - Array of sponsor data records
 * @param {string} conferenceName - Selected conference name
 * @param {string} selectedYear - Selected year or "All"
 * @returns {number} Total revenue for the client/conference/year combination
 */
export const calculateConferenceRevenue = (clientName, sponsorData, conferenceName, selectedYear) => {
  if (!clientName || !sponsorData || sponsorData.length === 0 || !conferenceName) {
    return 0;
  }

  // Filter sponsor data for this client and conference
  const matchingRecords = sponsorData.filter(record => 
    record.customerName === clientName && record.conferenceName === conferenceName
  );

  if (matchingRecords.length === 0) {
    return 0;
  }

  let totalRevenue = 0;

  // If "All Years", sum revenue across all years (2022-2026)
  if (selectedYear === 'All') {
    matchingRecords.forEach(record => {
      if (record.yearlyData) {
        Object.keys(record.yearlyData).forEach(year => {
          const yearData = record.yearlyData[year];
          if (yearData && typeof yearData.revenue === 'number') {
            totalRevenue += yearData.revenue;
          }
        });
      }
    });
  } else {
    // Specific year - sum revenue for that year across all sponsors
    matchingRecords.forEach(record => {
      if (record.yearlyData && record.yearlyData[selectedYear]) {
        const yearData = record.yearlyData[selectedYear];
        if (yearData && typeof yearData.revenue === 'number') {
          totalRevenue += yearData.revenue;
        }
      }
    });
  }

  return totalRevenue;
};

/**
 * Get revenue value for a client based on conference selection
 * @param {Object} client - Client object
 * @param {string} selectedConference - Selected conference or "All"
 * @param {string} selectedYear - Selected year or "All"
 * @param {Array} sponsorData - Sponsor data array
 * @returns {number} Revenue value
 */
export const getClientRevenue = (client, selectedConference, selectedYear, sponsorData) => {
  // If no conference selected (All Conferences), use main portfolio revenue
  if (!selectedConference || selectedConference === 'All') {
    // Get revenue from main portfolio data
    if (selectedYear === 'All') {
      // Use Total Revenue field
      const totalRevenue = client['Total Revenue'];
      if (typeof totalRevenue === 'string') {
        return parseFloat(totalRevenue.replace(/[$,]/g, '')) || 0;
      }
      return totalRevenue || 0;
    } else {
      // Use year-specific revenue
      const yearRevenue = client[`${selectedYear} Revenue`];
      if (typeof yearRevenue === 'string') {
        return parseFloat(yearRevenue.replace(/[$,]/g, '')) || 0;
      }
      return yearRevenue || 0;
    }
  }

  // Conference is selected - use sponsor sheet data
  return calculateConferenceRevenue(
    client.client_name,
    sponsorData,
    selectedConference,
    selectedYear
  );
};

/**
 * Format revenue column header based on conference and year selection
 * @param {string} selectedConference - Selected conference or "All"
 * @param {string} selectedYear - Selected year or "All"
 * @returns {string} Formatted column header
 */
export const getRevenueColumnHeader = (selectedConference, selectedYear) => {
  const year = selectedYear === 'All' 
    ? String(YEAR_CONFIG.lastDataYear)
    : selectedYear;

  if (!selectedConference || selectedConference === 'All') {
    return `Revenue (${year})`;
  }

  // Conference-specific revenue
  if (selectedYear === 'All') {
    return `${selectedConference} Revenue (All Years)`;
  }
  
  return `${selectedConference} Revenue (${year})`;
};

/**
 * Enrich clients with conference-specific revenue
 * @param {Array} clients - Array of client objects
 * @param {string} selectedConference - Selected conference or "All"
 * @param {string} selectedYear - Selected year or "All"
 * @param {Array} sponsorData - Sponsor data array
 * @returns {Array} Clients with updated revenue values
 */
export const enrichClientsWithConferenceRevenue = (clients, selectedConference, selectedYear, sponsorData) => {
  if (!clients || clients.length === 0) {
    return clients;
  }

  // If All Conferences, return clients as-is
  if (!selectedConference || selectedConference === 'All') {
    return clients;
  }

  // Enrich each client with conference-specific revenue
  return clients.map(client => ({
    ...client,
    conferenceRevenue: getClientRevenue(client, selectedConference, selectedYear, sponsorData),
    isConferenceRevenueActive: true
  }));
};

/**
 * Calculate conference-specific rooms for a client
 * @param {string} clientName - Client name (Customer rename from sponsor sheet)
 * @param {Array} sponsorData - Array of sponsor data records
 * @param {string} conferenceName - Selected conference name
 * @param {string} selectedYear - Selected year or "All"
 * @returns {number} Total rooms for the client/conference/year combination
 */
export const calculateConferenceRooms = (clientName, sponsorData, conferenceName, selectedYear) => {
  if (!clientName || !sponsorData || sponsorData.length === 0 || !conferenceName) {
    return 0;
  }

  // Filter sponsor data for this client and conference
  const matchingRecords = sponsorData.filter(record => 
    record.customerName === clientName && record.conferenceName === conferenceName
  );

  if (matchingRecords.length === 0) {
    return 0;
  }

  let totalRooms = 0;

  // If "All Years", sum rooms across all years (2022-2026)
  if (selectedYear === 'All') {
    matchingRecords.forEach(record => {
      if (record.yearlyData) {
        Object.keys(record.yearlyData).forEach(year => {
          const yearData = record.yearlyData[year];
          if (yearData && typeof yearData.rooms === 'number') {
            totalRooms += yearData.rooms;
          }
        });
      }
    });
  } else {
    // Specific year - sum rooms for that year across all sponsors
    matchingRecords.forEach(record => {
      if (record.yearlyData && record.yearlyData[selectedYear]) {
        const yearData = record.yearlyData[selectedYear];
        if (yearData && typeof yearData.rooms === 'number') {
          totalRooms += yearData.rooms;
        }
      }
    });
  }

  return totalRooms;
};

/**
 * Get rooms value for a client based on conference selection
 * @param {Object} client - Client object
 * @param {string} selectedConference - Selected conference or "All"
 * @param {string} selectedYear - Selected year or "All"
 * @param {Array} sponsorData - Sponsor data array
 * @returns {number} Rooms value
 */
export const getClientRooms = (client, selectedConference, selectedYear, sponsorData) => {
  // If no conference selected (All Conferences), use main portfolio rooms
  if (!selectedConference || selectedConference === 'All') {
    // Get rooms from main portfolio data
    if (selectedYear === 'All') {
      // Use group_size field (total rooms)
      return client.group_size || 0;
    } else {
      // Use year-specific rooms
      const yearRooms = client[`${selectedYear} Rooms`];
      return yearRooms || 0;
    }
  }

  // Conference is selected - use sponsor sheet data
  return calculateConferenceRooms(
    client.client_name,
    sponsorData,
    selectedConference,
    selectedYear
  );
};

/**
 * Enrich clients with conference-specific rooms
 * @param {Array} clients - Array of client objects
 * @param {string} selectedConference - Selected conference or "All"
 * @param {string} selectedYear - Selected year or "All"
 * @param {Array} sponsorData - Sponsor data array
 * @returns {Array} Clients with updated rooms values
 */
export const enrichClientsWithConferenceRooms = (clients, selectedConference, selectedYear, sponsorData) => {
  if (!clients || clients.length === 0) {
    return clients;
  }

  // If All Conferences, return clients as-is
  if (!selectedConference || selectedConference === 'All') {
    return clients;
  }

  // Enrich each client with conference-specific rooms
  return clients.map(client => ({
    ...client,
    conferenceRooms: getClientRooms(client, selectedConference, selectedYear, sponsorData),
    isConferenceRoomsActive: true
  }));
};

const conferenceRevenueService = {
  calculateConferenceRevenue,
  getClientRevenue,
  getRevenueColumnHeader,
  enrichClientsWithConferenceRevenue,
  calculateConferenceRooms,
  getClientRooms,
  enrichClientsWithConferenceRooms
};

export default conferenceRevenueService;
